import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    CircularProgress,
} from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { format } from 'date-fns';
import { salesOrdersAPI } from '../../api/salesOrders';
import { useSnackbar } from 'notistack';

interface ModificationRecord {
    id: number;
    field_changed: string;
    old_value: string;
    new_value: string;
    changed_by: string;
    changed_at: string;
    notes: string;
}

interface ModificationHistoryProps {
    soId: number;
}

const FIELD_LABELS: Record<string, string> = {
    delivery_date: 'Delivery Date',
    order_date: 'Order Date',
    items: 'Items',
    quantity: 'Quantity',
    unit_price: 'Price',
    notes: 'Notes',
    customer_id: 'Customer',
    order_source: 'Order Source',
    total_amount: 'Total Amount',
};

const ModificationHistory: React.FC<ModificationHistoryProps> = ({ soId }) => {
    const [history, setHistory] = useState<ModificationRecord[]>([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState<string>('all');
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        loadHistory();
    }, [soId]);

    const loadHistory = async () => {
        try {
            setLoading(true);
            const data = await salesOrdersAPI.getModificationHistory(soId);
            setHistory(data);
        } catch (error: any) {
            console.error('Failed to load modification history:', error);
            enqueueSnackbar('Failed to load modification history', { variant: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const filteredHistory = filter === 'all'
        ? history
        : history.filter((h) => h.field_changed === filter);

    const exportHistory = () => {
        // Convert to CSV
        const headers = ['Field', 'Old Value', 'New Value', 'Changed By', 'Changed At', 'Notes'];
        const rows = filteredHistory.map((record) => [
            FIELD_LABELS[record.field_changed] || record.field_changed,
            record.old_value || '',
            record.new_value || '',
            record.changed_by,
            format(new Date(record.changed_at), 'PPpp'),
            record.notes || '',
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
        ].join('\n');

        // Download
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `SO-${soId}-modification-history.csv`;
        a.click();
        window.URL.revokeObjectURL(url);

        enqueueSnackbar('Audit log exported', { variant: 'success' });
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (history.length === 0) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary">No modifications yet</Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Modification History</Typography>

                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Filter by Field</InputLabel>
                        <Select value={filter} onChange={(e) => setFilter(e.target.value)} label="Filter by Field">
                            <MenuItem value="all">All Changes</MenuItem>
                            <MenuItem value="delivery_date">Delivery Date</MenuItem>
                            <MenuItem value="items">Items</MenuItem>
                            <MenuItem value="quantity">Quantity</MenuItem>
                            <MenuItem value="unit_price">Price</MenuItem>
                            <MenuItem value="notes">Notes</MenuItem>
                        </Select>
                    </FormControl>

                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={exportHistory}
                        size="small"
                    >
                        Export
                    </Button>
                </Box>
            </Box>

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Field</TableCell>
                            <TableCell>Old Value</TableCell>
                            <TableCell>New Value</TableCell>
                            <TableCell>Changed By</TableCell>
                            <TableCell>Changed At</TableCell>
                            <TableCell>Notes</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredHistory.map((record) => (
                            <TableRow key={record.id} hover>
                                <TableCell>
                                    <Chip
                                        label={FIELD_LABELS[record.field_changed] || record.field_changed}
                                        size="small"
                                        variant="outlined"
                                    />
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textDecoration: 'line-through',
                                            color: 'error.main',
                                        }}
                                    >
                                        {record.old_value || '-'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        variant="body2"
                                        sx={{ color: 'success.main', fontWeight: 500 }}
                                    >
                                        {record.new_value || '-'}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">{record.changed_by}</Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2">
                                        {format(new Date(record.changed_at), 'MMM dd, yyyy')}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        {format(new Date(record.changed_at), 'hh:mm a')}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                        {record.notes || '-'}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Showing {filteredHistory.length} of {history.length} modifications
            </Typography>
        </Box>
    );
};

export default ModificationHistory;
